import { FlexCell, FlexRow } from "@/common/StyledComponents";
import {
    Autocomplete,
    Badge,
    Checkbox,
    CircularProgress,
    Collapse,
    Icon,
    IconButton,
    Link,
    Menu,
    MenuItem,
    Paper,
    Radio,
    TextField,
    Tooltip,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
    addAccountingAccount,
    checkBillLine,
    selectAccountingAccounts,
    selectBillLine,
    selectBillLineIsSearched,
    selectCanMultiCheckBillLines,
    selectProjectCurrency,
    selectTab,
    selectTaxCodes,
    selectToAllocateBillLines,
    setConfirmingRefutedBillLine,
    setDeallocatingBillLine,
    setRefutingBillLine,
    switchTab,
    uncheckBillLine,
    updateBillLine,
} from "./actualSlice";
import { ActualHeaderLine, ActualLine } from "./ActualLine";
import { Grid } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { downloadFile, formattedCurrency } from "@/utils";
import { useDispatch } from "react-redux";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { request } from "@/Api";
import ProtectedComponents from "@/auth/ProtectedComponents";
import {
    ACCOUNTING_BILL_STATUS_REFUTE_HST,
    ACCOUNTING_BILL_STATUS_REFUTE_OTHER,
    ACCOUNTING_BILL_STATUS_REFUTE_VENDOR,
} from "@/actuals/constants";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { useSearchParams } from "react-router-dom";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import { setError } from "@/nav/navSlice";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { selectSubscriptionId } from "@/auth/authSlice";
import { MAKERS_SUBSCRIPTION } from "@/common/SubscriptionComponent";

export function AccountingBillLine({
    billId,
    selectable,
    isLeaf,
    allocations,
    lineId,
}) {
    const dispatch = useDispatch();
    const ref = useRef();
    const [expanded, setExpanded] = useState(false);
    const [attachmentsAnchorEl, setAttachmentAnchorEl] = useState(null);
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const billLine = useSelector((state) => selectBillLine(state, billId));
    const bill = billLine.accounting_bill;
    const searched = useSelector((state) =>
        selectBillLineIsSearched(state, billId),
    );
    const allocated =
        billLine && billLine.accounting_bill_line_actual_lines.length > 0;
    const projectCurrency = useSelector(selectProjectCurrency);
    const categories = useSelector((state) => selectAccountingAccounts(state));
    const taxCodes = useSelector((state) => selectTaxCodes(state));
    const subscriptionId = useSelector((state) => selectSubscriptionId(state));
    const isMakers = subscriptionId == MAKERS_SUBSCRIPTION;

    const billCategoryOptions = categories[bill.legal_entity_id] || [];
    const billTaxCodeOptions = taxCodes[bill.legal_entity_id] || [];

    const menuOpen = Boolean(attachmentsAnchorEl);
    const actionMenuOpen = Boolean(actionsAnchorEl);

    useEffect(() => {
        if (!billLine) {
            return;
        }
        const goToBillId = searchParams.get("billId");

        if (goToBillId == billLine.id) {
            ref.current.scrollIntoView();
            if (billLine.accounting_bill_line_actual_lines.length > 1) {
                setExpanded(true);
            }
        }
    }, [searchParams]);

    if (!billLine || !searched) {
        return;
    }

    const lineAllocation =
        lineId && billLine
            ? billLine.accounting_bill_line_actual_lines.find(
                  (allocation) => allocation.actual_line_id == lineId,
              )
            : null;
    const lineAllocatedProject = lineAllocation
        ? lineAllocation.actual_project
        : 0;
    const lineAllocatedBill = lineAllocation ? lineAllocation.actual : 0;
    const isRefuted =
        billLine.status == ACCOUNTING_BILL_STATUS_REFUTE_HST ||
        billLine.status == ACCOUNTING_BILL_STATUS_REFUTE_OTHER ||
        billLine.status == ACCOUNTING_BILL_STATUS_REFUTE_VENDOR;

    const handleMenuOpen = (event) => {
        setAttachmentAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAttachmentAnchorEl(null);
    };

    const handleActionMenuOpen = (event) => {
        setActionsAnchorEl(event.currentTarget);
    };

    const handleActionMenuClose = () => {
        setActionsAnchorEl(null);
    };

    const hasActualLines =
        billLine.accounting_bill_line_actual_lines.length > 0;

    const startDeallocate = () => {
        dispatch(setDeallocatingBillLine(billLine));
    };

    const startRefute = () => {
        setActionsAnchorEl(null);
        dispatch(setRefutingBillLine(billLine));
    };

    const syncAccountingBill = () => {
        request
            .post("accounting-bills/external", null, {
                params: { accounting_bill_id: billId },
            })
            .then((response) => {
                dispatch(updateBillLine(response.data));
            })
            .catch((error) => {
                dispatch(setError({ message: error.response.data.detail }));
            });
    };

    const startConfirmRefute = () => {
        dispatch(setConfirmingRefutedBillLine(billLine));
    };

    const gotoGroup = () => {
        dispatch(switchTab(0));
        setSearchParams({ billId, _timestamp: Date.now() });
    };

    const handleBillUpdate = (field) => (event, obj) => {
        if (typeof obj == "object") {
            patchBill(field, obj);
        } else if (typeof obj == "string") {
            billAccountUpdateOrCreateByName(obj);
        } else {
            patchBill(field, event.target.value);
        }
    };

    const patchBill = (field, obj) => {
        let val;
        if (typeof obj == "object") {
            val = obj?.id;
        } else {
            val = event.target.value;
        }
        request
            .patch(`/accounting-bill-lines/${billLine.id}/`, {
                [field]: val || null,
            })
            .then(() => {
                const updatedBill = { ...billLine, [field]: val };
                if (field == "accounting_tax_code_id") {
                    updatedBill.accounting_tax_code = obj;
                } else if (field == "accounting_account_id") {
                    updatedBill.accounting_account = obj;
                }
                dispatch(updateBillLine(updatedBill));
            })
            .catch((error) => {
                console.log(error);
                dispatch(setError({ message: JSON.stringify(error) }));
            });
    };

    const handleBillAccountUpdateOrCreate = (event, obj) => {
        billAccountUpdateOrCreateByName(event.target.value);
    };

    const billAccountUpdateOrCreateByName = (name) => {
        const val = name;
        if (!val) {
            return;
        }
        const existingAccount = billCategoryOptions.find(
            (account) => account.name == val,
        );
        if (existingAccount) {
            const changed =
                existingAccount.id != billLine.accounting_account_id;
            if (changed) {
                patchBill("accounting_account_id", existingAccount);
            }
        } else {
            request
                .post(`/accounting-accounts/`, {
                    name: val,
                    legal_entity_id: billLine.legal_entity_id,
                })
                .then((response) => {
                    patchBill("accounting_account_id", response.data);
                    onBillAccountCreate(response.data);
                });
        }
    };

    const onBillAccountCreate = (account) => {
        dispatch(
            addAccountingAccount({
                legalEntityId: billLine.legal_entity_id,
                accountingAccount: account,
            }),
        );
    };

    return (
        <>
            <FlexRow style={{ paddingBottom: 10 }} ref={ref}>
                {selectable && (
                    <FlexCell style={{ maxWidth: 50 }}>
                        <AccountingBillLineToggle billLineId={billId} />
                    </FlexCell>
                )}
                <FlexCell minWidth={120}>
                    <Grid container alignItems={"center"} wrap="nowrap">
                        <Tooltip title={`id: ${billId}`}>
                            <Grid minWidth={100} item>
                                {bill.vendor_id ? (
                                    <Link href={`/vendors/${bill.vendor_id}`}>
                                        {bill.vendor_display_name || "-"}
                                    </Link>
                                ) : (
                                    <span>{bill.vendor_name || "-"}</span>
                                )}
                            </Grid>
                        </Tooltip>
                        {!isLeaf && (
                            <Grid item>
                                <IconButton
                                    onClick={() => {
                                        setExpanded(!expanded);
                                    }}
                                    disabled={!hasActualLines}
                                >
                                    <ArticleOutlinedIcon />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                </FlexCell>
                <FlexCell minWidth={80}>
                    <span>{bill.accounting_bill_number}</span>
                </FlexCell>
                <FlexCell minWidth={80}>
                    <span>{bill.invoice_date}</span>
                </FlexCell>
                <FlexCell item width={200} minWidth={100}>
                    {isMakers ? (
                        <span>{billLine.description || ""}</span>
                    ) : (
                        <TextField
                            size="small"
                            defaultValue={billLine.description || ""}
                            fullWidth
                            onBlur={handleBillUpdate("description")}
                        />
                    )}
                </FlexCell>
                {!isMakers && (
                    <FlexCell item width={200} minWidth={100}>
                        <Autocomplete
                            freeSolo
                            size="small"
                            value={billLine.accounting_account}
                            options={billCategoryOptions}
                            renderInput={(params) => (
                                <TextField fullWidth {...params} />
                            )}
                            onChange={handleBillUpdate("accounting_account_id")}
                            onBlur={handleBillAccountUpdateOrCreate}
                            getOptionLabel={(option) => {
                                if (typeof option == "object") {
                                    return option ? option.name : "";
                                } else if (typeof option == "string") {
                                    return option;
                                }
                            }}
                        />
                    </FlexCell>
                )}
                {!isMakers && (
                    <FlexCell item width={200} minWidth={100}>
                        <Autocomplete
                            size="small"
                            value={billLine.accounting_tax_code}
                            options={billTaxCodeOptions}
                            renderInput={(params) => (
                                <TextField fullWidth {...params} />
                            )}
                            onChange={handleBillUpdate(
                                "accounting_tax_code_id",
                            )}
                            getOptionLabel={(option) => {
                                return option ? option.name : "";
                            }}
                            isOptionEqualToValue={(option, value) => {
                                if (!option || !value) {
                                    return true;
                                }
                                return option.id == value.id;
                            }}
                        />
                    </FlexCell>
                )}
                {lineAllocation && (
                    <FlexCell item align="right">
                        <span>
                            {formattedCurrency(
                                lineAllocatedBill,
                                bill.currency,
                                false,
                                2,
                            )}
                        </span>
                    </FlexCell>
                )}
                {lineAllocation && (
                    <FlexCell item align="right">
                        <span>
                            {formattedCurrency(
                                lineAllocatedProject,
                                projectCurrency,
                                false,
                                2,
                            )}
                        </span>
                    </FlexCell>
                )}
                {!lineAllocation && (
                    <FlexCell item align="right">
                        <span>
                            {formattedCurrency(
                                billLine.amount,
                                bill.currency,
                                false,
                                2,
                            )}
                        </span>
                    </FlexCell>
                )}
                {!lineAllocation && (
                    <FlexCell item align="right">
                        <span>
                            {formattedCurrency(
                                billLine.actual_project,
                                projectCurrency,
                                false,
                                2,
                            )}
                        </span>
                    </FlexCell>
                )}
                {allocations && (
                    <FlexCell>
                        <FlexCell item align="right">
                            <span>
                                {formattedCurrency(
                                    allocations.find(
                                        (allocation) =>
                                            allocation.accounting_bill_line_id ==
                                            billId,
                                    ).actual,
                                    bill.currency,
                                    false,
                                    2,
                                )}
                            </span>
                        </FlexCell>
                    </FlexCell>
                )}
                <FlexCell minWidth={90} style={{ textAlign: "right" }}>
                    {billLine.status !=
                        ACCOUNTING_BILL_STATUS_REFUTE_VENDOR && (
                        <>
                            {lineId &&
                                billLine.accounting_bill_line_actual_lines
                                    .length > 1 && (
                                    <IconButton onClick={gotoGroup}>
                                        <CallSplitIcon />
                                    </IconButton>
                                )}
                            {!lineId &&
                                billLine.accounting_bill_line_actual_lines
                                    .length > 1 && (
                                    <IconButton
                                        onClick={() => {
                                            setExpanded(!expanded);
                                        }}
                                    >
                                        <CallSplitIcon />
                                    </IconButton>
                                )}
                            {isRefuted && (
                                <ProtectedComponents
                                    permission={
                                        "confirm_refuted_accounting_bill"
                                    }
                                >
                                    <IconButton onClick={startConfirmRefute}>
                                        <WarningRoundedIcon color="warning" />
                                    </IconButton>
                                </ProtectedComponents>
                            )}
                            {bill.attachments.length > 0 && (
                                <AttachmentButton
                                    openMenu={handleMenuOpen}
                                    attachment={
                                        bill.attachments.length == 1 &&
                                        bill.attachments[0]
                                    }
                                />
                            )}
                            {bill.file_url && (
                                <IconButton
                                    href={bill.file_url}
                                    target="_blank"
                                >
                                    <InsertDriveFileOutlinedIcon />
                                </IconButton>
                            )}
                            {allocated ? (
                                <IconButton onClick={startDeallocate}>
                                    <LinkOffIcon />
                                </IconButton>
                            ) : !isRefuted ? (
                                <IconButton onClick={handleActionMenuOpen}>
                                    <MoreVertIcon />
                                </IconButton>
                            ) : null}
                        </>
                    )}
                </FlexCell>
            </FlexRow>
            {!isLeaf && (
                <Collapse in={expanded}>
                    <Paper
                        sx={{
                            margin: "10px 10px 10px 10px",
                            padding: 3,
                            boxShadow: "none",
                            overflow: "auto",
                        }}
                    >
                        <ActualHeaderLine billId={billId} />
                        {billLine.accounting_bill_line_actual_lines.map(
                            (line, index) => (
                                <ActualLine
                                    lineId={line.actual_line_id}
                                    billId={billId}
                                    isLeaf={true}
                                    key={index}
                                />
                            ),
                        )}
                    </Paper>
                </Collapse>
            )}
            <Menu
                id="basic-menu"
                anchorEl={actionsAnchorEl}
                open={actionMenuOpen}
                onClose={handleActionMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button",
                }}
            >
                <div>
                    <MenuItem onClick={startRefute}>
                        {billLine.quickbooks_id != null
                            ? "Finance Refute"
                            : "Vendor Refute"}
                    </MenuItem>
                </div>
            </Menu>
        </>
    );
}

export function AccountingBillLineHeader({ selectable, isAllocating, lineId }) {
    const subscriptionId = useSelector((state) => selectSubscriptionId(state));
    const isMakers = subscriptionId == MAKERS_SUBSCRIPTION;
    return (
        <FlexRow style={{ color: "grey", paddingBottom: 20 }}>
            {selectable && <FlexCell item style={{ maxWidth: 50 }} />}
            <FlexCell item minWidth={120}>
                Vendor
            </FlexCell>
            <FlexCell item minWidth={80}>
                Invoice No.
            </FlexCell>
            <FlexCell item minWidth={80}>
                Date
            </FlexCell>
            <FlexCell item width={200} minWidth={100}>
                Notes
            </FlexCell>
            {!isMakers && (
                <FlexCell item width={200} minWidth={100}>
                    Category
                </FlexCell>
            )}
            {!isMakers && (
                <FlexCell item width={200} minWidth={100}>
                    Tax Code
                </FlexCell>
            )}
            {lineId && (
                <FlexCell item align="right">
                    Bill Allocated
                </FlexCell>
            )}
            {lineId && (
                <FlexCell item align="right">
                    Project Allocated
                </FlexCell>
            )}
            {!lineId && (
                <FlexCell item align="right">
                    Bill Total
                </FlexCell>
            )}
            {!lineId && (
                <FlexCell item align="right">
                    Project Total
                </FlexCell>
            )}
            {isAllocating && (
                <FlexCell item align="right">
                    Portion of Bill
                </FlexCell>
            )}
            <FlexCell item minWidth={90} align="right">
                Actions
            </FlexCell>
        </FlexRow>
    );
}

const AccountingBillLineToggle = ({ billLineId }) => {
    const dispatch = useDispatch();
    const toAllocateBillLines = useSelector(selectToAllocateBillLines);
    const multiSelect = useSelector(selectCanMultiCheckBillLines);
    const Toggle = multiSelect ? Checkbox : Radio;
    const handleToggle = (event, checked) => {
        checked
            ? dispatch(checkBillLine(billLineId))
            : dispatch(uncheckBillLine(billLineId));
    };
    return (
        <Toggle
            checked={
                toAllocateBillLines.find((b) => b.id == billLineId) != null
            }
            onChange={handleToggle}
            size="small"
        />
    );
};

export const AttachmentButton = ({ openMenu, attachment }) => {
    const [waiting, setWaiting] = useState(false);
    const handleAttachmentClick = (event) => {
        if (attachment) {
            setWaiting(true);
            downloadFile(
                `/accounting-bill-attachment/${attachment.legal_entity_id}/${attachment.quickbooks_id}/`,
                {},
                () => {
                    setWaiting(false);
                },
            );
        } else {
            openMenu(event);
        }
    };

    return (
        <>
            {waiting ? (
                <CircularProgress size={25} />
            ) : (
                <IconButton onClick={handleAttachmentClick}>
                    <PictureAsPdfIcon />
                </IconButton>
            )}
        </>
    );
};

const AttachmentMenuItem = ({ index, attachment }) => {
    const [waiting, setWaiting] = useState(false);
    const handleAttachmentClick = (legalEntityId, attachmentQuickbooksId) => {
        setWaiting(true);

        downloadFile(
            `/accounting-bill-attachment/${legalEntityId}/${attachmentQuickbooksId}/`,
            {},
            () => {
                setWaiting(false);
            },
        );
    };
    return (
        <>
            {waiting ? (
                <div style={{ padding: 2 }}>
                    <CircularProgress size={25} />
                </div>
            ) : (
                <Tooltip title={attachment.quickbooks_file_name} followCursor>
                    <MenuItem
                        disabled={waiting}
                        onClick={() => {
                            handleAttachmentClick(
                                attachment.legal_entity_id,
                                attachment.quickbooks_id,
                            );
                        }}
                    >
                        <span
                            style={{ fontSize: 12 }}
                        >{`View file ${index + 1}`}</span>
                    </MenuItem>
                </Tooltip>
            )}
        </>
    );
};
